import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { HelpCircle, X, Send, ArrowRight, Loader2, PlayCircle } from 'lucide-react';
import { BACKEND_URL, authHeader } from '../lib/supabase';
import { useApp } from '../store/useApp';

// Tutorial relacionado con la pantalla actual (id + segundo del capítulo).
const TUT_RUTA = [
  ['/onboarding', 'monta-tu-almacen', 40], ['/etiquetas', 'monta-tu-almacen', 170],
  ['/inventario', 'monta-tu-almacen', 225], ['/mapa', 'monta-tu-almacen', 285],
  ['/recepciones', 'tu-dia-a-dia', 0], ['/buscar', 'tu-dia-a-dia', 75],
  ['/entrada', 'tu-dia-a-dia', 130], ['/pedidos', 'tu-dia-a-dia', 195],
  ['/pendientes', 'tu-dia-a-dia', 305],
];
function tutorialDe(pathname) {
  for (const [pre, id, seg] of TUT_RUTA) { if (pathname.startsWith(pre) && pre !== '/') return `/tutoriales/${id}?t=${seg}`; }
  if (pathname === '/') return '/tutoriales/monta-tu-almacen?t=0';
  return null;
}

export function Ayuda() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const { tenantActivo } = useApp();
  const tid = tenantActivo?.tenant_id;
  const [abierto, setAbierto] = useState(false);
  const [pregunta, setPregunta] = useState('');
  const [mensajes, setMensajes] = useState([]); // {rol:'user'|'bot', texto, ruta?}
  const [cargando, setCargando] = useState(false);
  const finRef = useRef(null);

  const preguntar = async (e) => {
    e?.preventDefault?.();
    const q = pregunta.trim();
    if (!q || cargando) return;
    setMensajes((m) => [...m, { rol: 'user', texto: q }]);
    setPregunta(''); setCargando(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/ayuda`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Tenant-Id': tid || '', ...(await authHeader()) },
        body: JSON.stringify({ pregunta: q, idioma: i18n.language, session_id: `ayuda-${tid}`, ruta_actual: loc.pathname }),
      });
      if (!resp.ok) throw new Error(String(resp.status));
      const data = await resp.json();
      setMensajes((m) => [...m, { rol: 'bot', texto: data.respuesta, ruta: data.ruta || null }]);
    } catch (_) {
      setMensajes((m) => [...m, { rol: 'bot', texto: t('ayuda.error') }]);
    }
    setCargando(false);
    setTimeout(() => finRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  };

  return (
    <>
      <button className="btn ghost sm" onClick={() => setAbierto(true)} title={t('ayuda.titulo')} data-testid="ayuda-abrir-btn">
        {/* SVG inline (no lucide): garantiza render en iOS Safari */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: '0 0 auto', display: 'block' }} aria-hidden="true">
          <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      </button>

      {abierto && (
        <div className="ayuda-overlay" onClick={() => setAbierto(false)} data-testid="ayuda-overlay">
          <aside className="ayuda-panel" onClick={(e) => e.stopPropagation()} data-testid="ayuda-panel">
            <header className="ayuda-top">
              <div className="row" style={{ gap: 8 }}><HelpCircle size={18} /> <b>{t('ayuda.titulo')}</b></div>
              <button className="btn ghost sm" onClick={() => setAbierto(false)} data-testid="ayuda-cerrar-btn"><X size={16} /></button>
            </header>

            <div className="ayuda-msgs" data-testid="ayuda-mensajes">
              {mensajes.length === 0 && (
                <div className="ayuda-intro muted small" data-testid="ayuda-intro">
                  <div className="stack" style={{ gap: 6, marginBottom: 12 }}>
                    {tutorialDe(loc.pathname) && (
                      <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }}
                        onClick={() => { nav(tutorialDe(loc.pathname)); setAbierto(false); }} data-testid="ayuda-tutorial-rel">
                        <PlayCircle size={15} /> {t('tutoriales.ver_como')}
                      </button>
                    )}
                    <button className="btn ghost sm" style={{ width: '100%', justifyContent: 'flex-start' }}
                      onClick={() => { nav('/tutoriales'); setAbierto(false); }} data-testid="ayuda-tutoriales-todos">
                      <PlayCircle size={15} /> {t('tutoriales.titulo')}
                    </button>
                  </div>
                  <p>{t('ayuda.intro')}</p>
                  <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
                    {['ayuda.sug1', 'ayuda.sug2', 'ayuda.sug3'].map((k) => (
                      <button key={k} className="chip" onClick={() => { setPregunta(t(k)); }} data-testid={`ayuda-sugerencia-${k.slice(-1)}`}>{t(k)}</button>
                    ))}
                  </div>
                </div>
              )}
              {mensajes.map((m, i) => (
                <div key={i} className={`ayuda-msg ${m.rol}`} data-testid={`ayuda-msg-${m.rol}-${i}`}>
                  <p>{m.texto}</p>
                  {m.ruta && (
                    <button className="btn sm primary" onClick={() => { nav(m.ruta); setAbierto(false); }} data-testid={`ayuda-ir-${i}`}>
                      <ArrowRight size={14} /> {t('ayuda.ir')}
                    </button>
                  )}
                </div>
              ))}
              {cargando && <div className="ayuda-msg bot" data-testid="ayuda-cargando"><Loader2 size={16} className="girando" /></div>}
              <div ref={finRef} />
            </div>

            <form className="ayuda-input" onSubmit={preguntar}>
              <input value={pregunta} onChange={(e) => setPregunta(e.target.value)}
                placeholder={t('ayuda.placeholder')} data-testid="ayuda-input" />
              <button className="btn primary" disabled={!pregunta.trim() || cargando} data-testid="ayuda-enviar-btn"><Send size={16} /></button>
            </form>
          </aside>
        </div>
      )}
    </>
  );
}
